import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { Spinner, PageLoader } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import { Coins, Wrench, Check } from "lucide-react";

const SLOTS = [
  { key: "legs", label: "Legs" },
  { key: "torso", label: "Torso" },
  { key: "arms", label: "Arms" },
  { key: "head", label: "Head" },
];

export default function FabricationDepot() {
  const { pet, refreshPet, user, patchUser } = useOutletContext();
  const [groups, setGroups] = useState(null);
  const [owned, setOwned] = useState({}); // item_id -> qty
  const [slot, setSlot] = useState("legs");
  const [busy, setBusy] = useState(null);

  const loadInv = () => api.get("/inventory").then((r) => {
    const map = {};
    r.data.items.forEach((i) => { if (i.id.startsWith("part_")) map[i.id] = i.qty; });
    setOwned(map);
  });

  useEffect(() => {
    api.get("/parts").then((r) => setGroups(r.data.groups));
    loadInv();
  }, []);

  const equipped = useMemo(() => (pet ? { legs: pet.legs, torso: pet.torso, arms: pet.arms, head: pet.head } : {}), [pet]);

  if (!groups) return <PageLoader label="Booting the Fabrication Depot" />;

  const buy = async (it) => {
    setBusy(it.id);
    try {
      const { data } = await api.post("/shop/buy", { pet_id: it.id });
      patchUser({ credits: data.credits });
      playSfx("pop");
      toast.success(`Fabricated ${it.name} — stored in inventory.`);
      await loadInv();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const install = async (it) => {
    if (!pet) { toast.error("No active robot to install onto."); return; }
    setBusy(it.id);
    try {
      const { data } = await api.post("/inventory/use", { pet_id: it.id });
      playSfx("pop");
      toast.success(data.message);
      await refreshPet();
      await loadInv();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const list = groups[slot] || [];

  return (
    <div data-testid="fabrication-depot">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-black">Fabrication Depot</h1>
          <p className="label-cap">Buy parts, then install them onto your active robot</p>
        </div>
        <div className="flex items-center gap-2 panel px-4 py-2"><Coins className="w-4 h-4 text-secondary" /><span className="font-head font-black">{user?.credits ?? 0}</span></div>
      </div>

      <div className="grid lg:grid-cols-[minmax(220px,300px)_1fr] gap-5">
        {/* active bot preview */}
        <div className="panel-glow p-5 flex flex-col items-center justify-center hud-corner">
          <div className="label-cap mb-2 self-start">Active Unit</div>
          {pet ? (
            <>
              <LayeredStack layers={pet.layers} core={pet.core_color} rig={pet.rig} size={260} state={pet.thermal >= 100 || pet.battery <= 0 ? "off" : "idle"} />
              <div className="font-head font-bold mt-3">{pet.name}</div>
              <div className="label-cap mt-1">{pet.temperament}</div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-10">Fabricate a robot first to install parts.</p>
          )}
        </div>

        {/* parts browser */}
        <div>
          <div className="panel px-2 py-2 mb-4 flex items-center gap-1 overflow-x-auto no-scrollbar">
            {SLOTS.map((s) => (
              <button key={s.key} data-testid={`slot-tab-${s.key}`} onClick={() => setSlot(s.key)}
                className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${slot === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {list.map((it) => {
              const isEquipped = equipped[it.part_type] === it.part_id;
              const isOwned = (owned[it.id] || 0) > 0;
              const afford = (user?.credits ?? 0) >= it.price;
              return (
                <div key={it.id} data-testid={`part-${it.id}`} className={`panel p-3 flex flex-col ${isEquipped ? "panel-glow" : ""}`}>
                  <div className="w-full aspect-[0.67] bg-background/60 rounded-[var(--radius)] overflow-hidden grid place-items-center mb-2">
                    <img src={it.art} alt={it.name} draggable={false} className="w-full h-full object-contain" />
                  </div>
                  <div className="font-head font-bold text-xs leading-none">{it.name}</div>
                  <div className="label-cap text-[9px] mt-1 flex-1">{it.rarity}{isOwned ? ` · owned x${owned[it.id]}` : ""}</div>
                  {isEquipped ? (
                    <button disabled className="btn-ghost w-full mt-2 text-xs opacity-70"><Check className="w-3.5 h-3.5" /> Installed</button>
                  ) : isOwned ? (
                    <button data-testid={`install-${it.id}`} onClick={() => install(it)} disabled={busy === it.id || !pet} className="btn-primary w-full mt-2 text-xs">
                      {busy === it.id ? <Spinner className="w-4 h-4" /> : <><Wrench className="w-3.5 h-3.5" /> Install</>}
                    </button>
                  ) : (
                    <button data-testid={`buy-${it.id}`} onClick={() => buy(it)} disabled={busy === it.id || !afford} className={afford ? "btn-secondary w-full mt-2 text-xs" : "btn-ghost w-full mt-2 text-xs opacity-60"}>
                      {busy === it.id ? <Spinner className="w-4 h-4" /> : <><Coins className="w-3.5 h-3.5" /> {it.price}</>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
